import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useState } from "react";

import { ClientSideSuspense } from "scmp-app/components/common/client-side-suspense";
import { PostiesStudentFormMessageBox } from "scmp-app/components/posties/student-form/message-box";
import type { StudentFormFields } from "scmp-app/components/posties/student-form/types";

import { PostiesAlertBox } from "./alert-box";
import { usePostiesFormDrawerContext } from "./contexts";
import { getStudentForm } from "./helpers";
import { useStudentFormSchema } from "./hooks";
import {
  CloseButton,
  CloseContainer,
  Container,
  FormContainer,
  MessageContainer,
  Peter,
  StyledDrawer,
  StyledPostiesStudentForm,
  StyledSvgBottom,
  Title,
  TitleContainer,
  Wrapper,
} from "./styles";

type Props = {
  className?: string;
};

export const PostiesFormDrawer: FunctionComponent<Props> = ({ className }) => {
  const [fields, setFields] = useState<null | StudentFormFields>(null);
  const { formData, formDrawer, setFormDrawer, setShowAlertBox } = usePostiesFormDrawerContext();
  const { asPath, replace } = useRouter();
  const studentFormFields = useStudentFormSchema();

  const handleCloseDrawer = useCallback(() => {
    const url = new URL(location.origin + asPath);
    if (!!url.hash) {
      url.hash = "";
      void replace(url.toString(), undefined, { scroll: false, shallow: true });
    }
    setFormDrawer?.(previous => ({
      ...previous,
      documentId: null,
      isLatestForm: false,
      open: false,
    }));
  }, [asPath, replace, setFormDrawer]);

  useEffect(() => {
    if (formData) getStudentForm({ formData, setFields, studentFormFields });
  }, [formData, studentFormFields]);

  return (
    <>
      <StyledDrawer anchor="right" open={formDrawer.open}>
        <Container className={className}>
          {formDrawer.messageBox === null && (
            <CloseContainer>
              <CloseButton onClick={() => setShowAlertBox?.(true)} />
            </CloseContainer>
          )}

          {formDrawer.messageBox ? (
            <MessageContainer>
              <div />
              <PostiesStudentFormMessageBox
                onCloseDrawer={handleCloseDrawer}
                variant={formDrawer.messageBox}
              />
              <div>
                <StyledSvgBottom />
              </div>
            </MessageContainer>
          ) : (
            <Wrapper>
              <>
                <TitleContainer>
                  <Title>
                    {formData?.restData.title === ""
                      ? "Submit your fun news captions"
                      : formData?.restData.title}{" "}
                  </Title>
                  <Peter />
                </TitleContainer>
                <FormContainer>
                  <ClientSideSuspense fallback="Loading...">
                    {fields && (
                      <>
                        <StyledPostiesStudentForm fields={fields} />
                        <div>
                          <StyledSvgBottom />
                        </div>
                      </>
                    )}
                  </ClientSideSuspense>
                </FormContainer>
              </>
            </Wrapper>
          )}
        </Container>
      </StyledDrawer>
      <PostiesAlertBox onClose={handleCloseDrawer} />
    </>
  );
};

PostiesFormDrawer.displayName = "PostiesFormDrawer";
